import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format, isAfter } from "date-fns";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  CheckCircle,
  Close,
  RadioButtonUnchecked,
} from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { Stream } from "../../../types/streams";
import { ISearchStreamDialogProps } from "../types/reviews";
import Paginator, { PaginationProps } from "../../../components/ui/Paginator";

interface Params {
  page: number;
  page_size: number;
  user_id: number | null;
};

export const SearchStreamDialog: React.FC<ISearchStreamDialogProps> = ({
  user,
  searchEventOpen,
  setSearchEventOpen,
  searchResults,
  setSearchResults,
  setCreateReviewOpen,
}) => {
  const [streamOptions, setStreamOptions] = useState<Stream[]>([]);
  const [streamsCount, setStreamsCount] = useState<number>(0);
  const [streamsLoading, setStreamsLoading] = useState(false);

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 0,
    pageSize: 5
  });

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const closeAndReset = () => {
    setSearchEventOpen(false);
    setStreamOptions([]);
  };

  useEffect(() => {
    const fetchStreams = async () => {
      if (!user?.id || !searchEventOpen) return;
      setStreamsLoading(true);
      try {
        const apiPage = pagination.page + 1;
        const params: Params = {
          page: apiPage,
          page_size: pagination.pageSize,
          user_id: user.id
        };
        const streams = await apiService.get<{ count: number, results: Stream[] }>(
          `/api/streams/stream/fullfilled-with-user/`, params
        );

        setStreamsCount(streams.data.count);

        setStreamOptions(
          streams.data.results.filter(
            (strm) => !isAfter(strm.date, new Date())
          ) || []
        );
      } catch (err) {
        console.error("Failed to fetch streams:", err);
        setStreamOptions([]);
      } finally {
        setStreamsLoading(false);
      }
    };

    if (searchEventOpen && user?.id) {
      fetchStreams();
    }
  }, [searchEventOpen, user?.id, pagination.page, pagination.pageSize]);

  return (
    <Dialog
      open={searchEventOpen}
      onClose={closeAndReset}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Select Stream
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography
            variant={"subtitle2"}
          >{`Streams by: ${user?.name}`}</Typography>
          {streamOptions?.length === 0 ? (
            <Chip
              size={"small"}
              sx={{ borderRadius: 1, mt: 1, textTransform: "uppercase" }}
              label={<i>{`${user?.name} hasn't fulfilled a stream yet`}</i>}
            />
          ) : (
            <Box
              sx={{
                padding: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                my: 1,
              }}
            >
              {streamOptions && pagination ? (streamOptions.map((stream: Stream) => {
                return (
                  <Box
                    key={stream.id}
                    onClick={() => setSearchResults(stream)}
                    // define selected and unselected backgrounds for this box
                    sx={{
                      borderWidth: 1,
                      mt: 1,
                      backgroundColor:
                        stream.id === searchResults?.id
                          ? reduxTheme.primary_colors[400]
                          : reduxTheme.primary_colors[800],
                      borderStyle: "solid",
                      color:
                        stream.id === searchResults?.id
                          ? reduxTheme.primary_colors[900]
                          : reduxTheme.primary_colors[100],
                      cursor: "pointer",
                      borderColor:
                        stream.id === searchResults?.id
                          ? reduxTheme.primary_colors[400]
                          : reduxTheme.primary_colors[800],
                      borderRadius: 2,
                      padding: 1,
                    }}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      {stream.id === searchResults?.id ? (
                        <CheckCircle />
                      ) : (
                        <RadioButtonUnchecked />
                      )}
                      <Typography variant={"subtitle2"}>
                        {stream.title}
                      </Typography>
                    </Box>
                    <Typography variant={"caption"}>
                      {format(stream.date, "dd/MM/yyyy")}
                    </Typography>
                  </Box>
                );
              })) : null}

              {pagination ? (
                <Paginator
                  count={streamsCount}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              ) : null}
            </Box>
          )}
          <List
            sx={{
              backgroundColor: reduxTheme.primary_colors[800],
              borderRadius: 3,
              mt: 1,
              maxHeight: 300,
              overflowY: "auto",
              minHeight: 40,
            }}
          >
            {streamsLoading ? (
              <ListItem>
                <CircularProgress
                  size={20}
                  sx={{ mr: 2, color: reduxTheme.primary_colors[100] }}
                />
                <ListItemText
                  primary="Fetching..."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : !searchResults ? (
              <ListItem>
                <ListItemText
                  primary="No stream selected."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : (
              <ListItem>
                <Typography
                  variant={"subtitle2"}
                >{`ID: ${searchResults?.id} - ${searchResults?.title}`}</Typography>
              </ListItem>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Discard
        </Button>
        <Button
          startIcon={<AddCircleOutlineOutlined />}
          sx={{
            color: reduxTheme.primary_colors[900],
            backgroundColor: reduxTheme.primary_colors[400],
          }}
          onClick={() => {
            closeAndReset();
            setCreateReviewOpen(true);
          }}
        >
          Add Pilot Review
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchStreamDialog;
